// Create: contexts/ConsentContext.tsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

interface ConsentContextType {
  mappConsent: boolean | null; // null = loading, true/false = actual state
  isLoading: boolean;
  refreshConsent: () => Promise<void>;
}

const ConsentContext = createContext<ConsentContextType | undefined>(undefined);

export const useConsent = () => {
  const context = useContext(ConsentContext);
  if (!context) {
    throw new Error('useConsent must be used within ConsentProvider');
  }
  return context;
};

export const ConsentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mappConsent, setMappConsent] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshConsent = async () => {
    const sessionUid = localStorage.getItem("session_uid");
    const userUid = localStorage.getItem("user_uid");
    
    if (!sessionUid || !userUid) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/user-profile`, {
        headers: {
          Accept: "application/json",
          "user-uid": userUid,
          "target-user-uid": userUid,
        },withCredentials:true,
      });

      const profileData = response.data?.data;
      if (profileData) {
        const consentValue = profileData.mapp_consent;
        localStorage.setItem("mapp_consent", consentValue ? "true" : "false");
        localStorage.setItem("is_initial_login", profileData.is_initial_login ? "true" : "false");
        setMappConsent(consentValue);
      }
    } catch (err) {
      console.error("Failed to fetch user profile", err);
      // Use localStorage as fallback
      const storedConsent = localStorage.getItem("mapp_consent");
      setMappConsent(storedConsent === "true");
    } finally {
      setIsLoading(false);
    }
  };

useEffect(() => {
  const sessionUid = localStorage.getItem("session_uid");
  const userUid = localStorage.getItem("user_uid");

  // Don't proceed if not logged in
  if (!sessionUid || !userUid) {
    setIsLoading(false);
    return;
  }

  const storedConsent = localStorage.getItem("mapp_consent");
  if (storedConsent !== null) {
    setMappConsent(storedConsent === "true");
    setIsLoading(false);
  } else {
    refreshConsent();
  }
}, []);


  return (
    <ConsentContext.Provider value={{ mappConsent, isLoading, refreshConsent }}>
      {children}
    </ConsentContext.Provider>
  );
};